import { LessonPlanItemType, PlaylistItemType } from "./../../../../../models/master-plan";
import { Form, Select, Button, AutoComplete, Radio, Slider } from "ant-design-vue";
import { WrappedFormUtils, FieldDecoratorOptions } from "ant-design-vue/lib";
import "ant-design-vue/dist/antd.less";
import { Component, Vue, Prop } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { EmptyTransition, TransitionModel, TransitionModelPropName } from "@/services/transition/model";
import {
	disabledDueToAccessLevel,
	getRouteAccessLevel,
	minute2Second,
	pureRichText,
	spaceValidator,
	getConversationTitle
} from "@/util/func";
import { CommonEntitiesLocale, CommonLocale, LessonPlanLocale } from "@/locales/localeid";
import { fmtMsg, ActionItem, GLGlobal } from "commonui";
import MasterPlanState from "@/modules/master-plan";
import RichTextSingleLineEditor from "@/components/rich-text-single-line-editor/rich-text-single-line-editor.vue";
import DurationSelect from "@/components/content-exposure/duration-select/duration-select.vue";
import TransitionList from "../transition-list/transition-list.vue";
import { EmptyRepConversation, IContentConversationRepModel, ILessonPlanItemModel } from "@/models/lesson-plan";
import { Activity, LessonPlanVersionStatus } from "@/util/enum";
import { EmptyGuid } from "@/util/constants";
import { AccountService } from "@/services";

@Component({
	components: {
		"a-form": Form,
		"a-form-item": Form.Item,
		"a-select": Select,
		"a-select-option": Select.Option,
		"a-auto-complete": AutoComplete,
		"a-button": Button,
		RichTextSingleLineEditor,
		DurationSelect,
		TransitionList,
		ActionItem,
		Radio,
		RadioGroup: Radio.Group,
		Slider
	}
})
export default class DetailContent extends Vue {
	@Prop() transitions!: TransitionModel[];
	@Prop() repConversations!: IContentConversationRepModel[];
	@Prop() lessonTransitions!: ILessonPlanItemModel[];
	@Prop({ default: false }) isREPMethodology?: boolean;
	@Prop({ default: 1 }) conversationsSpeed?: number;
	private masterPlanModule = getModule(MasterPlanState);
	private maxTransitionTextLength: number = 500;
	private transitionSelectorFieldName: string = "transitionSelector";
	private conversationSelectorFieldName: string = "conversationSelector";
	private transitionTitle: string = fmtMsg(CommonEntitiesLocale.CommonModalTransition);
	private transitionRequireMsg: string = fmtMsg(CommonLocale.CommonFieldRequired, { name: this.transitionTitle });
	private transitionsInitialValue: string = "";
	private conversationsInitialValue: string = EmptyGuid;
	private transitionInitialValue: string = "<b>Transition:</b>&nbsp;";
	private durationTitle: string = fmtMsg(CommonEntitiesLocale.CommonTansitionDuration);
	private durationRequireMsg: string = fmtMsg(CommonLocale.CommonFieldRequired, { name: this.durationTitle });
	private durationInitialValue: string = "1:00";
	private isAddingTransition: boolean = false;
	private canAddTransition: boolean = false;
	private detailForm: WrappedFormUtils;
	public inputEntityText: string = "";
	public validEntityText: any = null;
	public selectedItemType: Activity = Activity.Question;
	public speedSliderMarks = {
		0.6: "0.6x",
		0.7: "0.7x",
		0.8: "0.8x",
		0.9: "0.9x",
		1.0: "1x"
	};
	public playlistCoversationsSpeed?: number = this.conversationsSpeed;
	private studentPortalTestUXUrl: string = "testconversation/conversation/:conversationId";
	public isTestUXAllowed = false;

	created(): void {
		this.detailForm = this.$form.createForm(this, { name: "detailForm" });
	}

	async mounted() {
		const userId = GLGlobal.loginInfo()?.profile?.sub;
		if (!userId) {
			return;
		}
		const isAllowed = await AccountService.isTestingAllowed(userId);
		if (isAllowed) {
			this.isTestUXAllowed = true;
		}
	}

	get commonLocale(): any {
		return CommonLocale;
	}

	get commonEntitiesLocale(): any {
		return CommonEntitiesLocale;
	}

	get lessonPlanLocale() {
		return LessonPlanLocale;
	}

	get canAddLesson(): boolean {
		return this.masterPlanModule.currentLessonPlanVersion.status === LessonPlanVersionStatus.Draft;
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	get isDisabled(): boolean {
		return !this.canAddLesson || this.disabledDueToAccessLevel;
	}

	get isAddConversationDisabled(): boolean {
		return this.masterPlanModule.currentRepConversation.contentId === EmptyRepConversation.contentId;
	}

	get isSliderDisabled(): boolean {
		return this.masterPlanModule.currentLessonPlanVersion.status === LessonPlanVersionStatus.Published;
	}

	get currentTransitions(): TransitionModel[] {
		return this.transitions.filter(
			(t) => t.id === EmptyTransition.id || (t.isCommon === true && t.vcsLevelId === null)
		);
	}

	get currentRepConversations(): IContentConversationRepModel[] {
		const contentIds = this.currentLessonTransitions
			.filter((t) => {
				t.itemType === LessonPlanItemType.Activity;
			})
			.map((t) => t.componentId);

		return this.repConversations.filter(
			(r) => r.contentId === EmptyRepConversation.contentId || !contentIds.includes(r.contentId)
		);
	}

	get currentLessonTransitions(): any {
		return this.lessonTransitions;
	}

	get transitionsDecorator(): [string, FieldDecoratorOptions] {
		return [
			this.transitionSelectorFieldName,
			{
				initialValue: this.transitionsInitialValue
			}
		];
	}

	checkConversationSelectedValue(rule: any, value: string, callback: (msg?: string) => void) {
		if (value === EmptyGuid) {
			callback(fmtMsg(this.lessonPlanLocale.LessonPlanMasterPlanManageConversationSelectCorrectMessage));
		}
		callback();
	}

	get conversationsDecorator(): [string, FieldDecoratorOptions] {
		return [
			this.conversationSelectorFieldName,
			{
				initialValue: this.conversationsInitialValue,
				rules: [
					{
						required: true,
						message: fmtMsg(
							this.lessonPlanLocale.LessonPlanMasterPlanManageConversationSelectRequiredMessage
						)
					},
					{ validator: this.checkConversationSelectedValue, trigger: "change" }
				]
			}
		];
	}

	get transitionDecorator(): [string, FieldDecoratorOptions] {
		return [
			TransitionModelPropName.Text,
			{
				initialValue: this.transitionInitialValue,
				rules: [
					{
						required: true,
						message: this.transitionRequireMsg,
						trigger: "change"
					},
					{
						max: this.maxTransitionTextLength,
						message: fmtMsg(CommonLocale.CommonFieldLength, {
							name: this.transitionTitle,
							value: this.maxTransitionTextLength
						}),
						trigger: "change"
					},
					{
						validator: (rule, value, callback) => {
							return spaceValidator(
								this.detailForm,
								TransitionModelPropName.Text,
								this.transitionRequireMsg,
								rule,
								value,
								callback
							);
						}
					}
				]
			}
		];
	}

	get durationDecorator(): [string, FieldDecoratorOptions] {
		return [
			TransitionModelPropName.Duration,
			{
				initialValue: this.durationInitialValue,
				rules: [
					{
						required: true,
						message: this.durationRequireMsg,
						trigger: "change"
					}
				]
			}
		];
	}

	get playListItemType() {
		return PlaylistItemType;
	}

	get activityType() {
		return Activity;
	}

	getTransitionTitle(transition: TransitionModel) {
		return pureRichText(transition.text);
	}

	searchValue(transition: TransitionModel) {
		return `${transition.text}\t${transition.id}`;
	}

	formatMessage(id: string, params?: any): string {
		return fmtMsg(id, params);
	}

	onSelectedTransitionChanged(value) {
		this.isAddingTransition = value === EmptyTransition.id;
		this.canAddTransition = value.length > 0;
		const transition: TransitionModel = this.masterPlanModule.transitions.find(
			(t) => t.id === value
		) as TransitionModel;
		this.masterPlanModule.setTransition(transition);
		this.$emit("selectedTransitionChanged", transition);
	}

	onSelectedConversationChanged(value) {
		const conversation: IContentConversationRepModel = this.masterPlanModule.repConversations.find(
			(t) => t.contentId == value
		) as IContentConversationRepModel;
		this.masterPlanModule.setRepConversation(conversation);
	}

	onFilterOption(input, option) {
		const element = option.componentOptions.children[0].elm;
		return element && element.innerText && element.innerText.toUpperCase().indexOf(input.toUpperCase()) >= 0;
	}

	onResetFormFieldsValue(): void {
		this.detailForm.resetFields();
		this.isAddingTransition = false;
		this.masterPlanModule.setRepConversation(EmptyRepConversation);
		this.selectedItemType = Activity.Question;
	}

	onAddTransition() {
		if (!this.canAddTransition) {
			return;
		}
		const addTransition = (transition, isCustom, selectedItemType) => {
			this.masterPlanModule.addTransition({ transition, isCustom, selectedItemType });
			this.onResetFormFieldsValue();
			this.canAddTransition = false;
		};
		if (this.isAddingTransition) {
			this.detailForm.validateFields((errors, values) => {
				if (errors) {
					return;
				} else {
					const transition: TransitionModel = {
						...values,
						duration: minute2Second(values.duration)
					} as TransitionModel;
					addTransition(transition, this.isAddingTransition, this.selectedItemType);
				}
			});
		} else {
			addTransition(this.masterPlanModule.currentTransition, false, this.selectedItemType);
		}
		this.$emit("transitionAdded");
	}

	onAddQuestion() {
		this.masterPlanModule.addQuestion();
		this.$emit("transitionAdded");
	}
	onAddSelectedActivity() {
		this.masterPlanModule.addSelectedActivity();
		this.$emit("transitionAdded");
	}
	getConversationTitleValue(repConversation, isFromSelect) {
		return getConversationTitle(repConversation, isFromSelect);
	}
	onAddConversation() {
		this.detailForm.validateFields((errors, values) => {
			if (errors) {
				return;
			} else {
				const compId = this.masterPlanModule.currentRepConversation.contentId;
				const name = getConversationTitle(this.masterPlanModule.currentRepConversation, false);
				this.masterPlanModule.addConversation({ compId, name });
				this.detailForm.resetFields();
				this.masterPlanModule.setRepConversation(EmptyRepConversation);
			}
		});
		this.$emit("transitionAdded");
	}

	onLessonSequencesChanged(data) {
		this.$emit("lessonSequencesChanged", data);
	}

	onRemoveTransition(transition) {
		this.$emit("removeTransition", transition);
	}

	onSubmitTransitionForm(success: (values: any) => void, error: (errors: any, values: any) => void): void {
		this.detailForm.validateFields((errors, values) => {
			const { transitionSelector, ...rest } = values;
			if (errors) {
				error(errors, rest);
			} else {
				success({ ...rest, duration: minute2Second(rest.duration) });
			}
		});
	}

	onSpeedChange() {
		this.$emit("transitionAdded");
	}

	handleTestUX() {
		const urlPrefix = process.env.VUE_APP_STUDENT_PORTAL_URL;
		const convId = this.masterPlanModule.currentRepConversation.conversationId.toString();
		// Commented as per GL-13101
		//const speed = this.playlistCoversationsSpeed?.toString() as string;
		const url = urlPrefix + this.studentPortalTestUXUrl.replace(":conversationId", convId);
		window.open(url, "_blank");
	}

	getCurrentActivityAddText() {
		return `${fmtMsg(LessonPlanLocale.LessonPlanMasterPlanManageActivityAddButtonText)} ${
			this.masterPlanModule.currentSelectedActivity
		}`;
	}

	isActivityTransition(value): boolean {
		return !!(value == Activity.Transition);
	}

	isActivityQuestion(value): boolean {
		return !!(value == Activity.Question);
	}

	isActivityConversation(value): boolean {
		return !!(value == Activity.Conversation);
	}

	isActivityNew(value): boolean {
		if (value != Activity.Conversation && value != Activity.Question && value != Activity.Transition) {
			return true;
		}
		return false;
	}
}
